import React, { useState, useEffect } from 'react'

import * as S from './styles'

import { useServices } from 'presentation/hooks/use-services'
import { useStores } from 'presentation/hooks/use-stores'
import { toast } from 'react-toastify'
import { Document } from 'domain/entities/document'
import { DocumentsList } from 'common/enum/documents-list'
import DocumentAccordeon from 'presentation/shared/layouts/DocumentAccordeon'
import { renameFile } from 'common/utils/file/renameFile'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { ReportsCard } from 'presentation/shared/layouts/ReportsCard'
import {
  OpenSurgicalOrderPatientDocument,
  PatientDocument
} from 'common/enum/patient-document'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import UploadButtonNew from 'presentation/shared/components/UploadButtonNew'
import downloadFileFromBlob from 'common/utils/downloadFileFromBlob'
import moment from 'moment-timezone'
import { useFormikContext } from 'formik'
import { useHistory } from 'react-router'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import { observer } from 'mobx-react'
import { useDocumentUploadAndPolling } from 'presentation/hooks/useDocumentUploadAndPolling'
import { getUploadLogger } from 'common/utils/upload-logger'

const logger = getUploadLogger()

type MappedDocumentEntry = {
  type: string
  status?: string
  documents: Array<{
    type: string
    status?: string
    sended_at?: string
    status_updated_by?: { name?: string }
  }>
}

export interface ReportAndDocumentsFormValues {
  reportRecommendation: string
}

export const ReportsAndDocumentsFormV2 = WithLoading(
  observer(({ setIsLoading }: WithLoadingProps) => {
    const formik = useFormikContext<ReportAndDocumentsFormValues>()
    const surgicalOrderService = useServices().surgicalOrder
    const store = useStores().surgicalOrderV2
    const currentAccount = useStores().currentAccount.getCurrentAccount()
    const [informationAccordionOpen, setInformationAccordionOpen] = useState('')
    const history = useHistory()

    const uploadService = surgicalOrderService.getUploadService()
    const { uploadAndPoll, resetState } =
      useDocumentUploadAndPolling(uploadService)

    const documentsList = () => {
      const mappedDocs = mappedDocuments()

      return Object.keys(OpenSurgicalOrderPatientDocument).map((document) => {
        const docs = mappedDocs
          .filter((doc) => doc.type === document)
          .sort((a, b) => {
            if (
              moment(a.documents[0]?.sended_at).isAfter(
                b.documents[0]?.sended_at
              )
            )
              return -1
            else if (
              moment(a.documents[0]?.sended_at).isBefore(
                b.documents[0]?.sended_at
              )
            )
              return 1
            else return 0
          })

        const filesList = docs.reduce<MappedDocumentEntry['documents']>(
          (acc, curr) => [...acc, ...curr.documents],
          []
        )

        return {
          label: DocumentsList[document as keyof typeof DocumentsList],
          type: document,
          status: docs[0]?.status,
          documents: filesList
        }
      })
    }

    const mappedDocuments = (): MappedDocumentEntry[] => {
      return (
        store.surgicalOrder.documents?.map((document: Document) => ({
          type: document.type,
          status: document.status,
          documents: [
            {
              ...document,
              sended_at: document.sendedAt,
              status_updated_by: {
                name: document.updated_by?.name
              }
            }
          ]
        })) || []
      )
    }

    const handleChangeInformationAccordion = (
      label: string,
      isExpanded: boolean
    ) => {
      setInformationAccordionOpen(!isExpanded ? '' : label)
    }

    const uploadDocument = async (files: File[], type: string) => {
      const file = files[0]

      try {
        setIsLoading(true)
        resetState()
        const newFile = renameFile(file, file.name)

        const user = {
          id: currentAccount?.user?.user_id || 0,
          name: currentAccount?.user?.name || '',
          token: currentAccount?.accessToken || ''
        }

        const surgicalOrderId = store.surgicalOrder?.surgicalOrderId as number

        const result = await uploadAndPoll(
          newFile,
          surgicalOrderId,
          type as PatientDocument,
          user,
          {
            pollOnce: async ({ documentId, fileName, type: docType }) => {
              const response = await surgicalOrderService.getSurgicalOrderById({
                surgical_order_id: store.surgicalOrder?.surgicalOrderId
              })

              const documentUpload = response.documents.find(
                (doc: Document) => {
                  const hasUuidInName = doc.name.includes(documentId)
                  const hasExactNameMatch = doc.name === fileName
                  const nameMatches = hasUuidInName || hasExactNameMatch
                  const typeMatches = doc.type === docType

                  return nameMatches && typeMatches
                }
              )

              if (documentUpload) {
                store.updateStoreSurgicalOrder({
                  documents: response.documents
                })
                return true
              }

              return false
            }
          }
        )

        if (!result.documentId) {
          throw new Error('Falha no upload do documento')
        }
      } catch (error) {
        logger.error('Erro no upload do documento', error, {
          surgicalOrderId: store.surgicalOrder?.surgicalOrderId,
          fileName: file.name,
          documentType: type
        })
        const message =
          error instanceof Error
            ? error.message
            : 'Ocorreu um erro ao enviar o documento'
        toast.error(message)
      } finally {
        setIsLoading(false)
      }
    }

    const deleteDocument = async (document_id: number) => {
      setIsLoading(true)
      try {
        await surgicalOrderService.deleteUploadedDocumentsNew({
          document_id
        })

        await store.loadSurgicalOrder(
          store.surgicalOrder?.surgicalOrderId,
          surgicalOrderService
        )
      } catch (error) {
        toast.error('Ocorreu um erro ao deletar o documento')
      } finally {
        setIsLoading(false)
      }
    }

    const downloadDocument = async (
      document_id: number,
      _group_id: string,
      type: keyof typeof DocumentsList,
      fileName?: string
    ) => {
      setIsLoading(true)
      try {
        const surgicalOrderId = store.surgicalOrder?.surgicalOrderId as number

        const file = await surgicalOrderService.loadSurgicalOrderDocument({
          surgical_order_id: surgicalOrderId,
          document_id
        })

        if (file) {
          const originalExtension = fileName?.split('.').pop()?.toLowerCase()
          const extension = originalExtension || file.extension || ''
          const downloadName = extension
            ? `${DocumentsList[type]}.${extension}`
            : DocumentsList[type]

          downloadFileFromBlob(file.data, file.contentType, downloadName)
        }
      } catch {
        toast.error('Ocorreu um erro ao baixar o documento')
      } finally {
        setIsLoading(false)
      }
    }

    const filteredReports = store.surgicalOrder.documents?.filter(
      (document) =>
        document.type === OpenSurgicalOrderPatientDocument['Laudos dos exames']
    )

    const documentsNotToShow = [
      'LAUDO DE EXAME',
      'Laudos dos exames',
      'PLANEJAMENTO CIRÚRGICO',
      'Guia de autorização'
    ]

    // TODO: Centralizar essa função de redirect no store
    useEffect(() => {
      if (!store.surgicalOrder?.surgicalOrderId) history.push('/home')
    }, [])

    return (
      <S.Wrapper>
        <HeadingNew color="textSecondary">Laudos</HeadingNew>
        <SupportTextNew fontFamily="secondary" color="textSecondary">
          Adicione os laudos necessários. Você <b>também deve indicar laudos</b>{' '}
          que estão no sistema do hospital.
        </SupportTextNew>

        <S.InputWrapper
          error={
            !!(
              formik.touched.reportRecommendation &&
              formik.errors.reportRecommendation
            )
          }
          style={{ marginTop: '8px' }}
        >
          <TextAreaNew
            label="Indicar laudo do sistema do hospital"
            placeholder="Identifique qual o laudo do sistema do hospital que você precisa"
            name="reportRecommendation"
            value={formik.values.reportRecommendation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.reportRecommendation &&
              formik.errors.reportRecommendation
            }
            onInputChange={formik.handleChange('reportRecommendation')}
            rows={3}
            maxLength={500}
          />
        </S.InputWrapper>

        {documentsList()?.map(
          (document) =>
            document.type ===
              OpenSurgicalOrderPatientDocument['Laudos dos exames'] && (
              <UploadButtonNew
                onInput={(files: unknown) =>
                  uploadDocument(files as File[], document.type)
                }
                name={document.type}
                outlined
                multiple={false}
                fullWidth
                size="large"
                style={{
                  marginTop: '16px',
                  display: 'flex',
                  justifyItems: 'center',
                  alignItems: 'center'
                }}
                disabled={
                  store.surgicalOrder?.reportRecommendation ? true : false
                }
              >
                {<S.IncludeReportsLabel>Incluir laudos</S.IncludeReportsLabel>}
              </UploadButtonNew>
            )
        )}
        {filteredReports.length > 0 && (
          <>
            <HeadingNew
              as="h3"
              color="textSecondary"
              size="small"
              style={{ marginTop: '24px' }}
            >
              {filteredReports.length >= 2
                ? 'Laudos anexados:'
                : 'Laudo anexado:'}
            </HeadingNew>

            {filteredReports.map(
              (document, index) =>
                index === 0 && (
                  <ReportsCard
                    key={document.type}
                    deleteDocument={deleteDocument}
                    documents={filteredReports}
                    downloadDocument={downloadDocument}
                    showStatus={false}
                  />
                )
            )}
          </>
        )}

        <HeadingNew as="h4" color="textSecondary" style={{ marginTop: '24px' }}>
          Outros documentos
        </HeadingNew>
        <SupportTextNew fontFamily="secondary" color="textSecondary">
          Adicione os documentos necessários ao processo de autorização do
          pedido cirúrgico.
        </SupportTextNew>
        <S.DocumentsWrapper>
          {documentsList()
            .filter((document) => !documentsNotToShow.includes(document.type))
            .map((document) => (
              <DocumentAccordeon
                gap="8px"
                key={document.type}
                document={document}
                shadow="xsatin"
                padding="16px"
                onDownloadFile={downloadDocument}
                onUploadFile={(files: unknown) =>
                  uploadDocument(files as File[], document.type)
                }
                onDeleteFile={deleteDocument}
                expandedDocument={informationAccordionOpen}
                handleChange={handleChangeInformationAccordion}
                uploadButton={true}
                showStatus={false}
              />
            ))}
        </S.DocumentsWrapper>
      </S.Wrapper>
    )
  })
)
